import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import PaymentIcon from '@mui/icons-material/Payment';
import DescriptionIcon from '@mui/icons-material/Description';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import { apiFetch } from '../../api/client';
import EditorTRModal from './EditorTRModal';
import EditorCIModal from './EditorCIModal';
import EditorNotificacaoModal from './EditorNotificacaoModal';
import {
  HUD_CARD,
  HUD_CTA_BUTTON,
  HUD_DIALOG_CONTENT,
  HUD_DIALOG_PAPER,
  HUD_DIALOG_TITLE,
  HUD_STEP_NUMBER,
} from './hudDialogStyles';
import {
  TIPOS_ACAO_ETAPA,
  TIPOS_ACAO_ETAPA_LABEL,
  normalizarTipoAcaoEtapa,
} from '../../pages/contratos/utils/fluxoEtapaTipos';
import {
  CAMPOS_TR_PADRAO,
  TIPOS_CAMPO_TR,
  normalizarCamposTr,
  novoCampoTr,
} from '../../pages/contratos/utils/camposTrFluxo';

const STATUS_LABELS = {
  EM_ANDAMENTO: 'Em andamento',
  EM_ANALISE: 'Em análise',
  REJEITADO: 'Rejeitado',
  CONCLUIDO: 'Concluído',
  CANCELADO: 'Cancelado',
  PENDENTE: 'Pendente',
};

const FLUXO_PAGAMENTO_SEED = {
  nome: 'Fluxo de Pagamento',
  descricao:
    'Fluxo padrão para processo de pagamento: atesto fiscal, avaliação, diretorias, auditoria e financeiro.',
  etapas: [
    {
      nome: 'Fiscal valida e atesta',
      descricao: 'Fiscal analisa o consolidado, atesta a NF e registra a avaliação.',
      ordem: 1,
      grupo: 'Fiscal',
      prazo: 3,
      status: 'EM_ANALISE',
      fiscalDinamico: true,
      exigeAssinatura: true,
      permiteAprovar: 1,
      permiteRejeitar: 1,
      permiteDevolver: 1,
      retornaInicio: 0,
    },
    {
      nome: 'Assinatura digital do fiscal',
      descricao: 'Assinatura do atesto/avaliação e do processo de pagamento.',
      ordem: 2,
      grupo: 'Fiscal',
      prazo: 2,
      status: 'EM_ANDAMENTO',
      fiscalDinamico: true,
      exigeAssinatura: true,
      permiteAprovar: 1,
      permiteRejeitar: 1,
      permiteDevolver: 1,
      retornaInicio: 0,
    },
    {
      nome: 'Diretoria / Gerência',
      descricao: 'Análise e assinatura das diretorias e gerência.',
      ordem: 3,
      grupo: 'Diretoria Executiva',
      prazo: 3,
      status: 'EM_ANALISE',
      fiscalDinamico: false,
      exigeAssinatura: true,
      permiteAprovar: 1,
      permiteRejeitar: 1,
      permiteDevolver: 1,
      retornaInicio: 0,
    },
    {
      nome: 'Auditoria',
      descricao: 'Conferência documental e conformidade.',
      ordem: 4,
      grupo: 'Auditoria',
      prazo: 3,
      status: 'EM_ANALISE',
      fiscalDinamico: false,
      exigeAssinatura: true,
      permiteAprovar: 1,
      permiteRejeitar: 1,
      permiteDevolver: 1,
      retornaInicio: 0,
    },
    {
      nome: 'Financeiro',
      descricao:
        'Liberação e registro do pagamento — setor financeiro da unidade (coordenador/analista), não diretor corporativo.',
      ordem: 5,
      grupo: 'Financeiro',
      prazo: 5,
      status: 'CONCLUIDO',
      fiscalDinamico: false,
      exigeAssinatura: true,
      permiteAprovar: 1,
      permiteRejeitar: 1,
      permiteDevolver: 1,
      retornaInicio: 0,
    },
  ],
};

function classificarFluxo(fluxo) {
  const n = String(fluxo?.nome || '').toLowerCase();
  if (n.includes('pagamento')) return 'PAGAMENTO';
  if (n.includes('aditivo')) return 'ADITIVO';
  if (n.includes('minuta')) return 'MINUTA';
  if (n.includes('contrata')) return 'CONTRATACAO';
  return 'OUTRO';
}

const TIPO_META = {
  CONTRATACAO: { label: 'Contratação', color: '#1d4ed8', bg: 'rgba(29,78,216,0.08)' },
  ADITIVO: { label: 'Aditivo', color: '#7c3aed', bg: 'rgba(124,58,237,0.08)' },
  MINUTA: { label: 'Minuta', color: '#0f766e', bg: 'rgba(13,148,136,0.1)' },
  PAGAMENTO: { label: 'Pagamento', color: '#c2410c', bg: 'rgba(234,88,12,0.1)' },
  OUTRO: { label: 'Outro', color: '#475569', bg: 'rgba(71,85,105,0.08)' },
};

const emptyFormEtapa = () => ({
  nome: '',
  descricao: '',
  grupo: '',
  responsaveis: [],
  prazo: '',
  tempoEstimado: '',
  status: 'EM_ANDAMENTO',
  cor: '',
  permiteAprovar: true,
  permiteRejeitar: true,
  permiteDevolver: true,
  retornaInicio: false,
  fiscalDinamico: false,
  exigeAssinatura: true,
  exigeConfiguracaoEdital: false,
  tipoAcao: 'APROVACAO_COM_ASSINATURA',
  prazoAlertaDias: '3',
  limiteReaberturas: '3',
});

/**
 * Painel central de Configuração de fluxos (Contratação, Aditivo, Minuta, Pagamento)
 * + atalhos para templates TR / CI / Notificação.
 */
export default function FluxosConfigPanel({ podeEditar = true }) {
  const [fluxos, setFluxos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fluxoAtualId, setFluxoAtualId] = useState(null);
  const [dialogDetalheOpen, setDialogDetalheOpen] = useState(false);
  const [editandoFluxo, setEditandoFluxo] = useState(false);
  const [fluxoEditado, setFluxoEditado] = useState(null);
  const [dialogEtapaOpen, setDialogEtapaOpen] = useState(false);
  const [etapaEditando, setEtapaEditando] = useState(null);
  const [formEtapa, setFormEtapa] = useState(emptyFormEtapa());
  const [dialogEditarTROpen, setDialogEditarTROpen] = useState(false);
  const [dialogEditarMinutaOpen, setDialogEditarMinutaOpen] = useState(false);
  const [dialogEditarMinutaAditivoOpen, setDialogEditarMinutaAditivoOpen] = useState(false);
  const [dialogEditarCIOpen, setDialogEditarCIOpen] = useState(false);
  const [dialogEditarNotificacaoOpen, setDialogEditarNotificacaoOpen] = useState(false);
  const [setores, setSetores] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loadingSetores, setLoadingSetores] = useState(false);
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [criandoPagamento, setCriandoPagamento] = useState(false);

  const fluxoAtual = useMemo(
    () => fluxos.find((f) => f.id === fluxoAtualId) || null,
    [fluxos, fluxoAtualId]
  );

  const carregarFluxos = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/api/contratos/fluxos');
      const data = await res.json().catch(() => []);
      if (!res.ok) throw new Error(data?.error || 'Erro ao carregar fluxos');
      setFluxos(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setFluxos([]);
      setSnackbar({ open: true, message: e.message || 'Erro ao carregar fluxos', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregarFluxos();
  }, [carregarFluxos]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingSetores(true);
      setLoadingUsuarios(true);
      try {
        const [rSetores, rUsuarios] = await Promise.all([
          apiFetch('/api/setores'),
          apiFetch('/api/usuarios'),
        ]);
        const dSetores = await rSetores.json().catch(() => []);
        const dUsuarios = await rUsuarios.json().catch(() => []);
        if (!cancelled) {
          setSetores(Array.isArray(dSetores) ? dSetores : []);
          setUsuarios(Array.isArray(dUsuarios) ? dUsuarios : []);
        }
      } catch (e) {
        if (!cancelled) console.error('Erro ao carregar setores/usuários:', e);
      } finally {
        if (!cancelled) {
          setLoadingSetores(false);
          setLoadingUsuarios(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const garantirFluxoPagamento = async () => {
    const existe = fluxos.some((f) => classificarFluxo(f) === 'PAGAMENTO');
    if (existe) {
      setSnackbar({ open: true, message: 'Fluxo de Pagamento já existe.', severity: 'info' });
      return;
    }
    setCriandoPagamento(true);
    try {
      const res = await apiFetch('/api/contratos/fluxos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(FLUXO_PAGAMENTO_SEED),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Erro ao criar fluxo de pagamento');
      setSnackbar({ open: true, message: 'Fluxo de Pagamento criado com etapas padrão.', severity: 'success' });
      await carregarFluxos();
      if (data?.id) {
        setFluxoAtualId(data.id);
        setDialogDetalheOpen(true);
      }
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Erro ao criar fluxo', severity: 'error' });
    } finally {
      setCriandoPagamento(false);
    }
  };

  const abrirDetalhe = (fluxo) => {
    setFluxoAtualId(fluxo.id);
    setDialogDetalheOpen(true);
  };

  const abrirEdicaoFluxo = (fluxoBase) => {
    const base = fluxoBase || fluxoAtual;
    if (!base) return;
    setFluxoEditado({
      id: base.id,
      nome: base.nome || '',
      descricao: base.descricao || '',
      camposTr: normalizarCamposTr(base.camposTr || base.campos_tr_json),
      etapas: (base.etapas || []).map((etapa, index) => {
        let responsaveis = [];
        try {
          const r = etapa.responsaveis_ids;
          if (r != null && r !== '') {
            responsaveis = typeof r === 'string' ? JSON.parse(r) : r;
            if (!Array.isArray(responsaveis)) responsaveis = [];
          }
        } catch (_) {
          /* ignore */
        }
        return {
          id: etapa.id,
          nome: etapa.nome || '',
          descricao: etapa.descricao || '',
          ordem: etapa.ordem || index + 1,
          grupo: etapa.grupo_responsavel || '',
          responsaveis,
          prazo: etapa.prazo_dias || '',
          tempoEstimado: etapa.tempo_estimado_dias ?? '',
          status: etapa.status_resultante || 'EM_ANDAMENTO',
          cor: etapa.cor || '',
          permiteAprovar: etapa.permite_aprovar !== 0,
          permiteRejeitar: etapa.permite_rejeitar !== 0,
          permiteDevolver: etapa.permite_devolver !== 0,
          retornaInicio: etapa.retorna_inicio_rejeicao === 1,
          fiscalDinamico:
            Number(etapa.fiscal_dinamico) === 1 ||
            /\bfiscal\b/i.test(String(etapa.grupo_responsavel || '')),
          exigeAssinatura: etapa.exige_assinatura !== 0,
          exigeConfiguracaoEdital:
            etapa.exigeConfiguracaoEdital === true ||
            Number(etapa.exige_configuracao_edital) === 1,
          tipoAcao: normalizarTipoAcaoEtapa(etapa.tipo_acao || etapa.tipoAcao, {
            exigeEdital: Number(etapa.exige_configuracao_edital) === 1,
            exigeAssinatura: etapa.exige_assinatura !== 0,
          }),
          prazoAlertaDias:
            etapa.prazo_alerta_dias != null ? String(etapa.prazo_alerta_dias) : etapa.prazoAlertaDias != null ? String(etapa.prazoAlertaDias) : '3',
          limiteReaberturas:
            etapa.limite_reaberturas != null
              ? String(etapa.limite_reaberturas)
              : etapa.limiteReaberturas != null
                ? String(etapa.limiteReaberturas)
                : '3',
        };
      }),
    });
    setEditandoFluxo(true);
  };

  const fecharEdicaoFluxo = () => {
    if (saving) return;
    setEditandoFluxo(false);
    setFluxoEditado(null);
    setEtapaEditando(null);
    setDialogEtapaOpen(false);
  };

  const adicionarEtapa = () => {
    if (!fluxoEditado) return;
    setFormEtapa(emptyFormEtapa());
    setEtapaEditando({ ordem: fluxoEditado.etapas.length + 1, isNew: true });
    setDialogEtapaOpen(true);
  };

  const editarEtapa = (etapa, index) => {
    let responsaveisArray = [];
    const raw = etapa.responsaveis ?? etapa.responsaveis_ids;
    if (raw != null) {
      if (Array.isArray(raw)) {
        responsaveisArray = raw.map((id) => Number(id)).filter((n) => !Number.isNaN(n));
      } else if (typeof raw === 'string' && raw.trim()) {
        try {
          const parsed = JSON.parse(raw);
          responsaveisArray = Array.isArray(parsed)
            ? parsed.map((id) => Number(id)).filter((n) => !Number.isNaN(n))
            : [];
        } catch {
          responsaveisArray = raw
            .split(',')
            .map((id) => parseInt(id, 10))
            .filter((n) => !Number.isNaN(n));
        }
      }
    }
    setFormEtapa({
      nome: etapa.nome || '',
      descricao: etapa.descricao || '',
      grupo: etapa.grupo ?? etapa.grupo_responsavel ?? '',
      responsaveis: responsaveisArray,
      prazo: etapa.prazo != null ? String(etapa.prazo) : etapa.prazo_dias != null ? String(etapa.prazo_dias) : '',
      tempoEstimado:
        etapa.tempoEstimado != null
          ? String(etapa.tempoEstimado)
          : etapa.tempo_estimado_dias != null
            ? String(etapa.tempo_estimado_dias)
            : '',
      status: etapa.status ?? etapa.status_resultante ?? 'EM_ANDAMENTO',
      cor: etapa.cor || '',
      permiteAprovar: etapa.permiteAprovar !== false && etapa.permite_aprovar !== 0,
      permiteRejeitar: etapa.permiteRejeitar !== false && etapa.permite_rejeitar !== 0,
      permiteDevolver: etapa.permiteDevolver !== false && etapa.permite_devolver !== 0,
      retornaInicio: etapa.retornaInicio === true || etapa.retorna_inicio_rejeicao === 1,
      fiscalDinamico:
        etapa.fiscalDinamico === true ||
        Number(etapa.fiscal_dinamico) === 1 ||
        /\bfiscal\b/i.test(String(etapa.grupo ?? etapa.grupo_responsavel ?? '')),
      exigeAssinatura: etapa.exigeAssinatura !== false && etapa.exige_assinatura !== 0,
      exigeConfiguracaoEdital:
        etapa.exigeConfiguracaoEdital === true ||
        Number(etapa.exige_configuracao_edital) === 1,
      tipoAcao: normalizarTipoAcaoEtapa(etapa.tipoAcao || etapa.tipo_acao, {
        exigeEdital:
          etapa.exigeConfiguracaoEdital === true ||
          Number(etapa.exige_configuracao_edital) === 1,
        exigeAssinatura: etapa.exigeAssinatura !== false && etapa.exige_assinatura !== 0,
      }),
      prazoAlertaDias:
        etapa.prazoAlertaDias != null
          ? String(etapa.prazoAlertaDias)
          : etapa.prazo_alerta_dias != null
            ? String(etapa.prazo_alerta_dias)
            : '3',
      limiteReaberturas:
        etapa.limiteReaberturas != null
          ? String(etapa.limiteReaberturas)
          : etapa.limite_reaberturas != null
            ? String(etapa.limite_reaberturas)
            : '3',
    });
    setEtapaEditando({ index, etapa, isNew: false });
    setDialogEtapaOpen(true);
  };

  const removerEtapa = (index) => {
    if (!fluxoEditado) return;
    if (!window.confirm('Deseja realmente remover esta etapa?')) return;
    const novas = fluxoEditado.etapas
      .filter((_, i) => i !== index)
      .map((e, i) => ({ ...e, ordem: i + 1 }));
    setFluxoEditado({ ...fluxoEditado, etapas: novas });
  };

  const moverEtapa = (index, direcao) => {
    if (!fluxoEditado) return;
    const novas = [...fluxoEditado.etapas];
    const novoIndex = direcao === 'up' ? index - 1 : index + 1;
    if (novoIndex < 0 || novoIndex >= novas.length) return;
    [novas[index], novas[novoIndex]] = [novas[novoIndex], novas[index]];
    novas.forEach((e, i) => {
      e.ordem = i + 1;
    });
    setFluxoEditado({ ...fluxoEditado, etapas: novas });
  };

  const salvarEtapa = () => {
    if (!fluxoEditado || !etapaEditando) return;
    if (!formEtapa.nome.trim()) {
      setSnackbar({ open: true, message: 'Informe o nome da etapa', severity: 'warning' });
      return;
    }
    const novas = [...fluxoEditado.etapas];
    const atualizada = {
      ...formEtapa,
      ordem: etapaEditando.ordem || etapaEditando.index + 1,
      id: etapaEditando.isNew ? null : etapaEditando.etapa?.id || null,
    };
    if (etapaEditando.isNew) novas.push(atualizada);
    else novas[etapaEditando.index] = atualizada;
    novas.forEach((e, i) => {
      e.ordem = i + 1;
    });
    setFluxoEditado({ ...fluxoEditado, etapas: novas });
    setDialogEtapaOpen(false);
    setEtapaEditando(null);
  };

  const salvarFluxo = async () => {
    if (!fluxoEditado) return;
    if (!fluxoEditado.nome.trim()) {
      setSnackbar({ open: true, message: 'Informe o nome do fluxo', severity: 'warning' });
      return;
    }
    if (!fluxoEditado.etapas.length) {
      setSnackbar({ open: true, message: 'Adicione ao menos uma etapa', severity: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        nome: fluxoEditado.nome.trim(),
        descricao: fluxoEditado.descricao?.trim() || null,
        camposTr: normalizarCamposTr(fluxoEditado.camposTr),
        etapas: fluxoEditado.etapas.map((etapa) => ({
          nome: etapa.nome.trim(),
          descricao: etapa.descricao?.trim() || null,
          ordem: etapa.ordem,
          grupo: etapa.grupo || null,
          responsaveis: Array.isArray(etapa.responsaveis) ? etapa.responsaveis : [],
          prazo: etapa.prazo ? parseInt(etapa.prazo, 10) : null,
          tempoEstimado:
            etapa.tempoEstimado != null && etapa.tempoEstimado !== ''
              ? parseInt(etapa.tempoEstimado, 10)
              : null,
          status: etapa.status || 'EM_ANDAMENTO',
          cor: etapa.cor || null,
          permiteAprovar: etapa.permiteAprovar ? 1 : 0,
          permiteRejeitar: etapa.permiteRejeitar ? 1 : 0,
          permiteDevolver: etapa.permiteDevolver ? 1 : 0,
          retornaInicio: etapa.retornaInicio ? 1 : 0,
          fiscalDinamico: etapa.fiscalDinamico ? 1 : 0,
          exigeAssinatura: etapa.exigeAssinatura !== false ? 1 : 0,
          exigeConfiguracaoEdital: etapa.exigeConfiguracaoEdital ? 1 : 0,
          tipoAcao: etapa.tipoAcao || 'APROVACAO_COM_ASSINATURA',
          prazoAlertaDias:
            etapa.prazoAlertaDias !== '' && etapa.prazoAlertaDias != null
              ? parseInt(etapa.prazoAlertaDias, 10)
              : null,
          limiteReaberturas:
            etapa.limiteReaberturas !== '' && etapa.limiteReaberturas != null
              ? parseInt(etapa.limiteReaberturas, 10)
              : 3,
        })),
      };
      const res = await apiFetch(`/api/contratos/fluxos/${fluxoEditado.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Erro ao salvar fluxo');
      setSnackbar({ open: true, message: 'Fluxo atualizado com sucesso!', severity: 'success' });
      setEditandoFluxo(false);
      setFluxoEditado(null);
      await carregarFluxos();
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Erro ao salvar fluxo', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const temPagamento = fluxos.some((f) => classificarFluxo(f) === 'PAGAMENTO');

  const indicadores = useMemo(() => {
    const byTipo = { CONTRATACAO: 0, ADITIVO: 0, MINUTA: 0, PAGAMENTO: 0, OUTRO: 0 };
    let totalEtapas = 0;
    fluxos.forEach((f) => {
      byTipo[classificarFluxo(f)] += 1;
      totalEtapas += (f.etapas || []).length;
    });
    return { byTipo, totalEtapas, totalFluxos: fluxos.length };
  }, [fluxos]);

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2.5, borderRadius: 2 }}>
        Centralize aqui os fluxos de Contratação, Aditivo, Minuta e Pagamento, além dos templates de TR,
        CI e Notificação. A engrenagem em Processo de Contratação continua disponível.
      </Alert>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Fluxos', value: indicadores.totalFluxos },
          { label: 'Etapas totais', value: indicadores.totalEtapas },
          { label: 'Contratação', value: indicadores.byTipo.CONTRATACAO },
          { label: 'Aditivo', value: indicadores.byTipo.ADITIVO },
          { label: 'Minuta', value: indicadores.byTipo.MINUTA },
          { label: 'Pagamento', value: indicadores.byTipo.PAGAMENTO },
        ].map((card) => (
          <Grid item xs={6} sm={4} md={2} key={card.label}>
            <Paper
              elevation={0}
              sx={{
                p: 1.75,
                borderRadius: 2.5,
                border: '1px solid rgba(15,23,42,0.08)',
                background: 'linear-gradient(165deg, #fff 0%, rgba(240,253,250,0.6) 100%)',
              }}
            >
              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                {card.label}
              </Typography>
              <Typography variant="h5" fontWeight={800} sx={{ color: '#0f766e' }}>
                {card.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={carregarFluxos}
          disabled={loading}
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          Atualizar
        </Button>
        {podeEditar && !temPagamento && (
          <Button
            variant="contained"
            startIcon={criandoPagamento ? <CircularProgress size={16} color="inherit" /> : <PaymentIcon />}
            onClick={garantirFluxoPagamento}
            disabled={criandoPagamento}
            sx={{ ...HUD_CTA_BUTTON, textTransform: 'none' }}
          >
            Criar Fluxo de Pagamento
          </Button>
        )}
        <Button
          variant="outlined"
          startIcon={<DescriptionIcon />}
          onClick={() => setDialogEditarTROpen(true)}
          sx={{ textTransform: 'none', fontWeight: 700, borderColor: 'rgba(124,58,237,0.45)', color: '#7c3aed' }}
        >
          Termo de Referência (TR)
        </Button>
        <Button
          variant="outlined"
          startIcon={<DescriptionIcon />}
          onClick={() => setDialogEditarMinutaOpen(true)}
          sx={{ textTransform: 'none', fontWeight: 700, borderColor: 'rgba(13,148,136,0.5)', color: '#0f766e' }}
        >
          Minuta
        </Button>
        <Button
          variant="outlined"
          startIcon={<DescriptionIcon />}
          onClick={() => setDialogEditarMinutaAditivoOpen(true)}
          sx={{ textTransform: 'none', fontWeight: 700, borderColor: 'rgba(124,58,237,0.45)', color: '#7c3aed' }}
        >
          Minuta para aditivo
        </Button>
        <Button
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={() => setDialogEditarCIOpen(true)}
          sx={{ textTransform: 'none', fontWeight: 700, borderColor: 'rgba(124,58,237,0.45)', color: '#7c3aed' }}
        >
          CI (moldura)
        </Button>
        <Button
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={() => setDialogEditarNotificacaoOpen(true)}
          sx={{ textTransform: 'none', fontWeight: 700, borderColor: 'rgba(124,58,237,0.45)', color: '#7c3aed' }}
        >
          Notificação
        </Button>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        TR, Minuta e Minuta de Aditivo usam cláusulas com (+) / (−) e IA. A Minuta pode importar o TR da unidade.
        Edição restrita ao Setor de Contratos (multiunidade). Pareceres seguem no ARCA.
      </Typography>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {fluxos.map((fluxo) => {
            const tipo = classificarFluxo(fluxo);
            const meta = TIPO_META[tipo];
            const qtd = (fluxo.etapas || []).length;
            return (
              <Grid item xs={12} md={6} key={fluxo.id}>
                <Paper
                  elevation={0}
                  sx={{
                    ...HUD_CARD,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.25,
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <AccountTreeIcon sx={{ color: meta.color }} />
                      <Typography variant="subtitle1" fontWeight={800}>
                        {fluxo.nome}
                      </Typography>
                    </Stack>
                    <Chip
                      size="small"
                      label={meta.label}
                      sx={{ fontWeight: 700, bgcolor: meta.bg, color: meta.color }}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                    {fluxo.descricao || 'Sem descrição.'}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip size="small" label={`${qtd} etapa(s)`} sx={{ fontWeight: 600 }} />
                    {(fluxo.etapas || []).slice(0, 3).map((e) => (
                      <Chip key={e.id || e.nome} size="small" variant="outlined" label={e.nome} />
                    ))}
                    {qtd > 3 && <Chip size="small" variant="outlined" label={`+${qtd - 3}`} />}
                  </Stack>
                  <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => abrirDetalhe(fluxo)}
                      sx={{ ...HUD_CTA_BUTTON, textTransform: 'none', py: 0.75 }}
                    >
                      Abrir configuração
                    </Button>
                    {podeEditar && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => abrirEdicaoFluxo(fluxo)}
                        sx={{ textTransform: 'none', fontWeight: 700 }}
                      >
                        Editar etapas
                      </Button>
                    )}
                  </Stack>
                </Paper>
              </Grid>
            );
          })}
          {!fluxos.length && (
            <Grid item xs={12}>
              <Alert severity="warning">Nenhum fluxo cadastrado.</Alert>
            </Grid>
          )}
        </Grid>
      )}

      {/* Detalhe do fluxo (mesmo padrão da engrenagem) */}
      <Dialog
        open={dialogDetalheOpen}
        onClose={() => setDialogDetalheOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: HUD_DIALOG_PAPER }}
      >
        <DialogTitle sx={HUD_DIALOG_TITLE}>
          <Typography variant="h6" fontWeight={800} sx={{ color: '#fff' }}>
            {classificarFluxo(fluxoAtual) === 'ADITIVO'
              ? 'Configuração do fluxo de Aditivo'
              : classificarFluxo(fluxoAtual) === 'PAGAMENTO'
                ? 'Configuração do fluxo de Pagamento'
                : 'Configuração do fluxo selecionado'}
          </Typography>
          {fluxoAtual?.nome && (
            <Typography variant="body2" sx={{ color: '#99f6e4', mt: 0.5, fontWeight: 600 }}>
              {fluxoAtual.nome}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers sx={HUD_DIALOG_CONTENT}>
          {fluxoAtual ? (
            <Stack spacing={2} sx={{ pt: 1 }}>
              {fluxoAtual.descricao && (
                <Typography variant="body2" color="text.secondary">
                  {fluxoAtual.descricao}
                </Typography>
              )}
              <Divider>
                <Typography variant="overline" sx={{ fontWeight: 800, color: '#0f766e', letterSpacing: 1 }}>
                  Etapas configuradas
                </Typography>
              </Divider>
              {(fluxoAtual.etapas || []).length ? (
                fluxoAtual.etapas.map((etapa, index) => (
                  <Box
                    key={etapa.id || index}
                    sx={{
                      ...HUD_CARD,
                      display: 'grid',
                      gridTemplateColumns: '40px 1fr',
                      gap: 1.5,
                      alignItems: 'flex-start',
                    }}
                  >
                    <Box sx={HUD_STEP_NUMBER}>{index + 1}</Box>
                    <Stack spacing={0.75}>
                      <Typography variant="subtitle2" fontWeight={800}>
                        {etapa.nome}
                      </Typography>
                      {etapa.descricao && (
                        <Typography variant="body2" color="text.secondary">
                          {etapa.descricao}
                        </Typography>
                      )}
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {etapa.grupo_responsavel && (
                          <Chip
                            label={`Responsável: ${etapa.grupo_responsavel}`}
                            size="small"
                            sx={{ fontWeight: 600, bgcolor: 'rgba(14,165,233,0.1)', color: '#0369a1' }}
                          />
                        )}
                        {etapa.prazo_dias && (
                          <Chip
                            label={`Prazo: ${etapa.prazo_dias} dia(s)`}
                            size="small"
                            sx={{ fontWeight: 600, bgcolor: 'rgba(234,88,12,0.1)', color: '#c2410c' }}
                          />
                        )}
                        {etapa.status_resultante && (
                          <Chip
                            label={`Status ao concluir: ${STATUS_LABELS[etapa.status_resultante] || etapa.status_resultante}`}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              bgcolor:
                                etapa.status_resultante === 'CONCLUIDO'
                                  ? 'rgba(13,148,136,0.12)'
                                  : 'rgba(15,23,42,0.06)',
                              color: etapa.status_resultante === 'CONCLUIDO' ? '#0f766e' : 'text.secondary',
                            }}
                          />
                        )}
                      </Stack>
                    </Stack>
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Nenhuma etapa cadastrada para este fluxo.
                </Typography>
              )}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Selecione um fluxo.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.75, flexWrap: 'wrap', gap: 1 }}>
          {podeEditar && (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => abrirEdicaoFluxo(fluxoAtual)}
              sx={{ ...HUD_CTA_BUTTON, mr: 0 }}
            >
              Editar fluxo
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => setDialogEditarTROpen(true)}
            sx={{ textTransform: 'none', fontWeight: 700, borderColor: 'rgba(124,58,237,0.45)', color: '#7c3aed' }}
          >
            Editar TR
          </Button>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => setDialogEditarCIOpen(true)}
            sx={{ textTransform: 'none', fontWeight: 700, borderColor: 'rgba(124,58,237,0.45)', color: '#7c3aed' }}
          >
            Editar CI
          </Button>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => setDialogEditarNotificacaoOpen(true)}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderColor: 'rgba(124,58,237,0.45)',
              color: '#7c3aed',
              mr: 'auto',
            }}
          >
            Editar Notificação
          </Button>
          <Button onClick={() => setDialogDetalheOpen(false)} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Editar fluxo */}
      <Dialog
        open={editandoFluxo}
        onClose={fecharEdicaoFluxo}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: HUD_DIALOG_PAPER }}
      >
        <DialogTitle sx={{ ...HUD_DIALOG_TITLE, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" fontWeight={800} sx={{ color: '#fff' }}>
              Editar Fluxo
            </Typography>
            <Typography variant="body2" sx={{ color: '#99f6e4', mt: 0.25 }}>
              Monte a sequência de etapas, prazos e responsáveis
            </Typography>
          </Box>
          <IconButton onClick={fecharEdicaoFluxo} size="small" sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={HUD_DIALOG_CONTENT}>
          {fluxoEditado ? (
            <Stack spacing={3} sx={{ pt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Nome do Fluxo"
                    fullWidth
                    value={fluxoEditado.nome}
                    onChange={(e) => setFluxoEditado({ ...fluxoEditado, nome: e.target.value })}
                    size="small"
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Descrição"
                    fullWidth
                    multiline
                    minRows={2}
                    value={fluxoEditado.descricao || ''}
                    onChange={(e) => setFluxoEditado({ ...fluxoEditado, descricao: e.target.value })}
                    size="small"
                  />
                </Grid>
              </Grid>

              {classificarFluxo(fluxoEditado) === 'CONTRATACAO' && (
                <>
                  <Divider>
                    <Typography variant="overline" sx={{ fontWeight: 800, color: '#0f766e', letterSpacing: 1 }}>
                      Campos do TR (formulário dinâmico)
                    </Typography>
                  </Divider>
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    Defina os campos preenchidos pelo fiscal no TR. Persistidos no servidor; o editor da
                    solicitação carrega este schema. O campo com chave <strong>objeto</strong> alimenta o
                    edital.
                  </Alert>
                  <Stack spacing={1.5}>
                    {(fluxoEditado.camposTr || CAMPOS_TR_PADRAO).map((campo, idx) => (
                      <Box
                        key={campo.id || idx}
                        sx={{
                          ...HUD_CARD,
                          p: 1.5,
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr 140px 100px 48px' },
                          gap: 1,
                          alignItems: 'center',
                        }}
                      >
                        <TextField
                          label="Rótulo"
                          size="small"
                          value={campo.label || ''}
                          onChange={(e) => {
                            const next = [...(fluxoEditado.camposTr || [])];
                            next[idx] = { ...next[idx], label: e.target.value };
                            setFluxoEditado({ ...fluxoEditado, camposTr: next });
                          }}
                        />
                        <TextField
                          label="Chave"
                          size="small"
                          value={campo.chave || ''}
                          onChange={(e) => {
                            const next = [...(fluxoEditado.camposTr || [])];
                            next[idx] = {
                              ...next[idx],
                              chave: e.target.value.replace(/\s+/g, '_').toLowerCase(),
                            };
                            setFluxoEditado({ ...fluxoEditado, camposTr: next });
                          }}
                          helperText={campo.chave === 'objeto' ? 'Usado no edital' : ' '}
                        />
                        <FormControl size="small" fullWidth>
                          <InputLabel>Tipo</InputLabel>
                          <Select
                            label="Tipo"
                            value={campo.tipo || 'texto'}
                            onChange={(e) => {
                              const next = [...(fluxoEditado.camposTr || [])];
                              next[idx] = { ...next[idx], tipo: e.target.value };
                              setFluxoEditado({ ...fluxoEditado, camposTr: next });
                            }}
                          >
                            {TIPOS_CAMPO_TR.map((t) => (
                              <MenuItem key={t.value} value={t.value}>
                                {t.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!campo.obrigatorio}
                              onChange={(e) => {
                                const next = [...(fluxoEditado.camposTr || [])];
                                next[idx] = { ...next[idx], obrigatorio: e.target.checked };
                                setFluxoEditado({ ...fluxoEditado, camposTr: next });
                              }}
                            />
                          }
                          label="Obrig."
                        />
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            const next = (fluxoEditado.camposTr || []).filter((_, i) => i !== idx);
                            setFluxoEditado({
                              ...fluxoEditado,
                              camposTr: next.length ? next : CAMPOS_TR_PADRAO.map((c) => ({ ...c })),
                            });
                          }}
                          aria-label="Remover campo"
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => {
                        const lista = fluxoEditado.camposTr || [];
                        setFluxoEditado({
                          ...fluxoEditado,
                          camposTr: [...lista, novoCampoTr(lista.length + 1)],
                        });
                      }}
                      sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
                    >
                      Adicionar campo TR
                    </Button>
                  </Stack>
                </>
              )}

              <Divider>
                <Typography variant="overline" sx={{ fontWeight: 800, color: '#0f766e', letterSpacing: 1 }}>
                  Etapas do Fluxo
                </Typography>
              </Divider>
              <Stack spacing={2}>
                {fluxoEditado.etapas.map((etapa, index) => (
                  <Box key={index} sx={{ ...HUD_CARD, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <Box sx={HUD_STEP_NUMBER}>{etapa.ordem || index + 1}</Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" fontWeight={800}>
                        {etapa.nome || 'Etapa sem nome'}
                      </Typography>
                      {etapa.descricao && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {etapa.descricao}
                        </Typography>
                      )}
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                        {etapa.grupo && (
                          <Chip
                            label={`Responsável: ${etapa.grupo}`}
                            size="small"
                            sx={{ fontWeight: 600, bgcolor: 'rgba(14,165,233,0.1)', color: '#0369a1' }}
                          />
                        )}
                        {etapa.prazo && (
                          <Chip
                            label={`Prazo: ${etapa.prazo} dia(s)`}
                            size="small"
                            sx={{ fontWeight: 600, bgcolor: 'rgba(234,88,12,0.1)', color: '#c2410c' }}
                          />
                        )}
                        <Chip
                          label={`Status: ${STATUS_LABELS[etapa.status] || etapa.status}`}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            bgcolor:
                              etapa.status === 'CONCLUIDO'
                                ? 'rgba(13,148,136,0.12)'
                                : 'rgba(15,23,42,0.06)',
                            color: etapa.status === 'CONCLUIDO' ? '#0f766e' : 'text.secondary',
                          }}
                        />
                      </Stack>
                    </Box>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="Mover para cima">
                        <span>
                          <IconButton size="small" onClick={() => moverEtapa(index, 'up')} disabled={index === 0}>
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Mover para baixo">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => moverEtapa(index, 'down')}
                            disabled={index === fluxoEditado.etapas.length - 1}
                          >
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Editar etapa">
                        <IconButton size="small" color="primary" onClick={() => editarEtapa(etapa, index)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remover etapa">
                        <IconButton size="small" color="error" onClick={() => removerEtapa(index)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>
                ))}
              </Stack>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={adicionarEtapa}
                fullWidth
                sx={{
                  textTransform: 'none',
                  fontWeight: 800,
                  py: 1.25,
                  borderStyle: 'dashed',
                  borderWidth: 2,
                  borderColor: 'rgba(13,148,136,0.45)',
                  color: '#0f766e',
                  bgcolor: 'rgba(13,148,136,0.04)',
                }}
              >
                Adicionar Etapa
              </Button>
            </Stack>
          ) : (
            <CircularProgress />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.75 }}>
          <Button onClick={fecharEdicaoFluxo} disabled={saving} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={salvarFluxo}
            disabled={saving}
            sx={HUD_CTA_BUTTON}
          >
            {saving ? 'Salvando...' : 'Salvar Fluxo'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Editar etapa */}
      <Dialog
        open={dialogEtapaOpen}
        onClose={() => !saving && setDialogEtapaOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: '1px solid rgba(15,118,110,0.12)',
            boxShadow: '0 16px 48px rgba(15, 23, 42, 0.16)',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
          {etapaEditando?.isNew ? 'Nova Etapa' : 'Editar Etapa'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Nome da Etapa"
              fullWidth
              value={formEtapa.nome}
              onChange={(e) => setFormEtapa({ ...formEtapa, nome: e.target.value })}
              size="small"
              required
            />
            <TextField
              label="Descrição"
              fullWidth
              multiline
              minRows={2}
              value={formEtapa.descricao}
              onChange={(e) => setFormEtapa({ ...formEtapa, descricao: e.target.value })}
              size="small"
            />
            <FormControl fullWidth size="small">
              <InputLabel>Setor/Grupo (opcional)</InputLabel>
              <Select
                value={formEtapa.grupo}
                label="Setor/Grupo (opcional)"
                onChange={(e) => setFormEtapa({ ...formEtapa, grupo: e.target.value })}
              >
                <MenuItem value="">Nenhum</MenuItem>
                {loadingSetores ? (
                  <MenuItem disabled>Carregando setores...</MenuItem>
                ) : (
                  (setores || []).map((setor) => (
                    <MenuItem key={setor.id} value={setor.nome}>
                      {setor.nome}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Responsáveis (usuários que podem autorizar/negar)</InputLabel>
              <Select
                multiple
                value={formEtapa.responsaveis}
                onChange={(e) => setFormEtapa({ ...formEtapa, responsaveis: e.target.value })}
                input={<OutlinedInput label="Responsáveis (usuários que podem autorizar/negar)" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((userId) => {
                      const usuario = usuarios.find((u) => u.id === userId);
                      return (
                        <Chip
                          key={userId}
                          label={usuario ? `${usuario.nome}${usuario.email ? ` (${usuario.email})` : ''}` : `ID: ${userId}`}
                          size="small"
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {loadingUsuarios ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} /> Carregando usuários...
                  </MenuItem>
                ) : (
                  usuarios.map((usuario) => (
                    <MenuItem key={usuario.id} value={usuario.id}>
                      <Checkbox checked={formEtapa.responsaveis.indexOf(usuario.id) > -1} />
                      <ListItemText primary={usuario.nome} secondary={usuario.email || usuario.setor || ''} />
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Prazo (dias)"
                  type="number"
                  fullWidth
                  value={formEtapa.prazo}
                  onChange={(e) => setFormEtapa({ ...formEtapa, prazo: e.target.value })}
                  size="small"
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Tempo Estimado (dias)"
                  type="number"
                  fullWidth
                  value={formEtapa.tempoEstimado}
                  onChange={(e) => setFormEtapa({ ...formEtapa, tempoEstimado: e.target.value })}
                  size="small"
                  inputProps={{ min: 1 }}
                />
              </Grid>
            </Grid>
            <FormControl fullWidth size="small">
              <InputLabel>Status ao Concluir</InputLabel>
              <Select
                value={formEtapa.status}
                label="Status ao Concluir"
                onChange={(e) => setFormEtapa({ ...formEtapa, status: e.target.value })}
              >
                {Object.entries(STATUS_LABELS).map(([key, label]) => (
                  <MenuItem key={key} value={key}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Divider />
            <Typography variant="subtitle2" fontWeight={700} color="primary">
              Tipo de ação da etapa
            </Typography>
            <Alert severity="info" sx={{ py: 0.5 }}>
              Configuração persistida no servidor (não fica no navegador). Define o comportamento da
              etapa no fluxo.
            </Alert>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo de ação</InputLabel>
              <Select
                label="Tipo de ação"
                value={formEtapa.tipoAcao || 'APROVACAO_COM_ASSINATURA'}
                onChange={(e) => {
                  const tipoAcao = e.target.value;
                  setFormEtapa((prev) => ({
                    ...prev,
                    tipoAcao,
                    exigeAssinatura:
                      tipoAcao === 'APROVACAO_COM_ASSINATURA' || tipoAcao === 'MINUTA_CLAUSULAS'
                        ? true
                        : prev.exigeAssinatura,
                    exigeConfiguracaoEdital:
                      tipoAcao === 'CONFIGURACAO_EDITAL' ? true : prev.exigeConfiguracaoEdital,
                  }));
                }}
              >
                {TIPOS_ACAO_ETAPA.map((t) => (
                  <MenuItem key={t} value={t}>
                    {TIPOS_ACAO_ETAPA_LABEL[t] || t}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {(formEtapa.tipoAcao === 'CONFIGURACAO_EDITAL' || formEtapa.exigeConfiguracaoEdital) && (
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Alerta (dias antes do encerramento)"
                    type="number"
                    fullWidth
                    size="small"
                    value={formEtapa.prazoAlertaDias}
                    onChange={(e) => setFormEtapa({ ...formEtapa, prazoAlertaDias: e.target.value })}
                    inputProps={{ min: 1, max: 90 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Limite de reaberturas de edital"
                    type="number"
                    fullWidth
                    size="small"
                    value={formEtapa.limiteReaberturas}
                    onChange={(e) => setFormEtapa({ ...formEtapa, limiteReaberturas: e.target.value })}
                    inputProps={{ min: 1, max: 10 }}
                    helperText="Padrão 3 (1º, 2º e 3º edital)"
                  />
                </Grid>
              </Grid>
            )}
            <Divider />
            <Typography variant="subtitle2" fontWeight={700} color="primary">
              Ações Permitidas
            </Typography>
            <Stack spacing={1.25} sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(15,118,110,0.04)' }}>
              {[
                {
                  key: 'fiscalDinamico',
                  label: 'Fiscal dinâmico (contrato)',
                  hint: 'Resolve o fiscal pelo contrato (ex.: Agimed → Manutenção → William)',
                },
                { key: 'exigeAssinatura', label: 'Exige assinatura digital nesta etapa' },
                { key: 'permiteAprovar', label: 'Permitir Autorizar/Aprovar' },
                { key: 'permiteRejeitar', label: 'Permitir Rejeitar' },
                { key: 'permiteDevolver', label: 'Permitir Devolver' },
                { key: 'retornaInicio', label: 'Retornar ao início quando rejeitado' },
                {
                  key: 'exigeConfiguracaoEdital',
                  label: 'Exige configuração de Edital',
                  hint: 'Ao avançar para esta etapa, solicita datas de abertura/encerramento do edital público',
                },
              ].map((item) => (
                <Box
                  key={item.key}
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {item.label}
                    </Typography>
                    {item.hint && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {item.hint}
                      </Typography>
                    )}
                  </Box>
                  <Switch
                    checked={Boolean(formEtapa[item.key])}
                    onChange={(e) => setFormEtapa({ ...formEtapa, [item.key]: e.target.checked })}
                    color="primary"
                  />
                </Box>
              ))}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogEtapaOpen(false)} disabled={saving} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={salvarEtapa}
            disabled={saving}
            startIcon={<SaveIcon />}
            sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 700 }}
          >
            Salvar Etapa
          </Button>
        </DialogActions>
      </Dialog>

      <EditorTRModal
        open={dialogEditarTROpen}
        onClose={() => setDialogEditarTROpen(false)}
        instrumentoTipo="tr"
        readOnly={!podeEditar}
        fluxoId={fluxoAtualId}
        processoId={null}
        escopo="fluxo"
      />
      <EditorTRModal
        open={dialogEditarMinutaOpen}
        onClose={() => setDialogEditarMinutaOpen(false)}
        instrumentoTipo="minuta"
        readOnly={!podeEditar}
        fluxoId={fluxoAtualId}
        processoId={null}
        escopo="fluxo"
      />
      <EditorTRModal
        open={dialogEditarMinutaAditivoOpen}
        onClose={() => setDialogEditarMinutaAditivoOpen(false)}
        instrumentoTipo="minuta-aditivo"
        readOnly={!podeEditar}
        fluxoId={fluxoAtualId}
        processoId={null}
        escopo="fluxo"
      />
      <EditorCIModal
        open={dialogEditarCIOpen}
        onClose={() => setDialogEditarCIOpen(false)}
        fluxoId={fluxoAtualId}
        processoId={null}
        escopo="fluxo"
      />
      <EditorNotificacaoModal
        open={dialogEditarNotificacaoOpen}
        onClose={() => setDialogEditarNotificacaoOpen(false)}
        fluxoId={fluxoAtualId}
        processoId={null}
        escopo="fluxo"
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
